import { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ZoomIn, ZoomOut, Maximize, RotateCcw, Circle, Square, Pentagon, Undo2, Check, X, ChevronLeft, ChevronRight, ImageOff, MapPin, Clock, Link2, AlertTriangle, Map, Navigation, Hand, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUIStore, useAnomalyStore, useInspectionStore, usePhotoStore } from '../../store';
import { AnnotationLayer } from './AnnotationLayer';
import { AnomalyForm } from './AnomalyForm';
import { PhotoThumbnail } from '../Common';
import type { AnomalyFormData } from './AnomalyForm';
import { usePhotoViewerShortcuts } from '../../hooks';
import type { PhotoAnnotation } from '../../types';
import { anomaliesApi, projectsApi, photosApi } from '../../lib/api';

export function PhotoViewer() {
  const containerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const galleryRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { projectId } = useParams<{ projectId: string }>();
  
  const { 
    currentPhoto, 
    selectedElementId, 
    selectedElementType, 
    isAnomalyFormOpen, 
    pendingAnomalyCoords,
    pendingAnnotationPoints,
    pendingAnnotationType,
    annotationMode,
    editingAnomalyId,
    setAnnotationMode,
    addAnnotationPoint,
    undoLastPoint,
    clearPendingAnnotation,
    openAnomalyForm,
    openAnomalyFormWithAnnotation,
    closeAnomalyForm,
    setCurrentPhoto,
    selectElement,
    highlightPhotoOnMap,
    editAnomaly,
  } = useUIStore();
  const { anomalies, getAnomaliesByPhoto } = useAnomalyStore();
  useInspectionStore();  // Used for context access via useUIStore
  const { getFilteredPhotos, getPhotoUrl, getPhotoById } = usePhotoStore();
  
  // Use filtered photos based on camera filter
  const photos = getFilteredPhotos();
  
  // State for handling stale blob URLs
  const [imageSrc, setImageSrc] = useState<string>('');
  const [imageLoadError, setImageLoadError] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  
  // State for rectangle drawing
  const [rectStartPoint, setRectStartPoint] = useState<{ x: number; y: number } | null>(null);
  const [rectEndPoint, setRectEndPoint] = useState<{ x: number; y: number } | null>(null);
  
  // State for photo review
  const [isReviewed, setIsReviewed] = useState(false);
  const [isMarkingReviewed, setIsMarkingReviewed] = useState(false);

  // Photo navigation
  const currentPhotoIndex = useMemo(() => {
    if (!currentPhoto || !currentPhoto.file || photos.length === 0) return -1;
    return photos.findIndex(p => p.filename === currentPhoto.file.name);
  }, [currentPhoto, photos]);

  const canGoPrev = currentPhotoIndex > 0;
  const canGoNext = currentPhotoIndex >= 0 && currentPhotoIndex < photos.length - 1;

  const goToPhoto = useCallback(async (index: number) => {
    if (index < 0 || index >= photos.length) return;
    const photo = photos[index];
    
    try {
      // Load full-size photo URL from server
      const photoUrl = await getPhotoUrl(photo.id, false); // false = full size
      
      if (!photoUrl) {
        console.error('Failed to get photo URL:', photo.filename);
        return;
      }

      // Fetch blob to create File object
      const response = await fetch(photoUrl);
      const blob = await response.blob();
      const file = new File([blob], photo.filename, { type: blob.type });
      
      setCurrentPhoto({
        id: photo.id,
        projectId: photo.projectId,
        file,
        dataUrl: photoUrl,
        width: photo.width,
        height: photo.height,
      });
      
      if (photo.elementId && photo.elementType) {
        selectElement(photo.elementId, photo.elementType);
      }
    } catch (e) {
      console.error('Failed to load photo:', photo.filename, e);
    }
  }, [photos, setCurrentPhoto, selectElement, getPhotoUrl]);

  const goPrevPhoto = useCallback(() => {
    if (canGoPrev) goToPhoto(currentPhotoIndex - 1);
  }, [canGoPrev, currentPhotoIndex, goToPhoto]);

  const goNextPhoto = useCallback(() => {
    if (canGoNext) goToPhoto(currentPhotoIndex + 1);
  }, [canGoNext, currentPhotoIndex, goToPhoto]);

  // Ref to store the latest goNextPhoto function for fast marking mode
  const goNextPhotoRef = useRef(goNextPhoto);
  useEffect(() => {
    goNextPhotoRef.current = goNextPhoto;
  }, [goNextPhoto]);

  // Function to advance to next photo (used after saving anomaly)
  const advanceToNextPhoto = useCallback(() => {
    // Auto-advance disabled
  }, []);
  
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  
  // Track if actual drag movement occurred (to distinguish clicks from drags)
  const didDragRef = useRef(false);
  const dragStartPosRef = useRef({ x: 0, y: 0 });
  const DRAG_THRESHOLD = 5; // Minimum pixels moved to count as drag
  
  // Mouse coordinates for HUD display
  const [mouseCoords, setMouseCoords] = useState<{ x: number; y: number } | null>(null);

  const zoomIn = useCallback(() => setScale((s) => Math.min(5, s * 1.2)), []);
  const zoomOut = useCallback(() => setScale((s) => Math.max(0.1, s / 1.2)), []);
  const resetView = useCallback(() => {
    setScale(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Get current photo metadata from store
  const currentPhotoData = useMemo(() => {
    if (!currentPhoto?.file) return null;
    return photos.find(p => p.filename === currentPhoto.file.name) || null;
  }, [currentPhoto, photos]);

  // Load isReviewed status when photo changes
  useEffect(() => {
    // First try to get from currentPhoto.id (direct from store)
    if (currentPhoto?.id) {
      const photoFromStore = getPhotoById(currentPhoto.id);
      if (photoFromStore?.isReviewed !== undefined) {
        setIsReviewed(photoFromStore.isReviewed);
        return;
      }
    }
    // Fallback to currentPhotoData
    if (currentPhotoData?.isReviewed !== undefined) {
      setIsReviewed(currentPhotoData.isReviewed);
    } else {
      setIsReviewed(false);
    }
  }, [currentPhoto?.id, currentPhotoData, getPhotoById]);

  // Get updatePhotoReviewStatus from photoStore
  const { updatePhotoReviewStatus } = usePhotoStore();

  // Handle marking photo as reviewed
  const handleToggleReviewed = useCallback(async () => {
    if (!currentPhoto?.id) return;
    
    setIsMarkingReviewed(true);
    try {
      const newStatus = !isReviewed;
      const response = await photosApi.review(currentPhoto.id, newStatus);
      setIsReviewed(newStatus);
      
      // Update the photo in the store so it persists
      updatePhotoReviewStatus(currentPhoto.id, newStatus);
      
      const data = response.data?.data;
      if (data) {
        toast.success(newStatus ? 'Photo marked as reviewed' : 'Photo marked as not reviewed', {
          description: `Project progress: ${data.projectProgress}% (${data.reviewedCount}/${data.totalCount})`,
        });
      }
    } catch (error) {
      console.error('Failed to update review status:', error);
      toast.error('Failed to update review status');
    } finally {
      setIsMarkingReviewed(false);
    }
  }, [currentPhoto?.id, isReviewed, updatePhotoReviewStatus]);

  // Navigate to map and highlight current photo
  const goToMapLocation = useCallback(() => {
    if (!currentPhotoData) return;
    
    // Highlight photo on map
    highlightPhotoOnMap(currentPhotoData.id);
    
    // Close photo viewer
    setCurrentPhoto(null);
    
    // Navigate to map tab
    if (projectId) {
      navigate(`/projects/${projectId}/map`);
    }
    
    toast.success('Showing photo location on map');
  }, [currentPhotoData, highlightPhotoOnMap, setCurrentPhoto, projectId, navigate]);
  
  // Load anomalies from server for current photo
  const [serverAnomalies, setServerAnomalies] = useState<any[]>([]);
  
  // Function to reload anomalies (can be called after creating new anomaly)
  const reloadAnomalies = useCallback(async () => {
    const effectiveProjectId = currentPhoto?.projectId || currentPhotoData?.projectId;
    
    if (!currentPhoto?.id || !effectiveProjectId) {
      setServerAnomalies([]);
      return;
    }
    
    try {
      const response = await projectsApi.getAnomalies(effectiveProjectId, {
        limit: 500,
      });
      
      const allAnomalies = response.data?.data || [];
      const photoAnomalies = allAnomalies.filter((a: { photoId?: string }) => a.photoId === currentPhoto.id);
      setServerAnomalies(photoAnomalies);
    } catch (error) {
      console.error('Failed to load anomalies:', error);
      setServerAnomalies([]);
    }
  }, [currentPhoto?.id, currentPhoto?.projectId, currentPhotoData?.projectId]);
  
  useEffect(() => {
    const projectId = currentPhoto?.projectId || currentPhotoData?.projectId;
    
    const loadAnomalies = async () => {
      if (!currentPhoto?.id || !projectId) {
        setServerAnomalies([]);
        return;
      }
      
      try {
        const response = await projectsApi.getAnomalies(projectId, {
          limit: 500,
        });
        
        // Filter anomalies for current photo on client side
        const allAnomalies = response.data?.data || [];
        const photoAnomalies = allAnomalies.filter((a: { photoId?: string }) => a.photoId === currentPhoto.id);
        setServerAnomalies(photoAnomalies);
      } catch (error) {
        console.error('Failed to load anomalies:', error);
        setServerAnomalies([]);
      }
    };
    
    loadAnomalies();
  }, [currentPhoto?.id, currentPhoto?.projectId, currentPhotoData?.projectId]);
  
  // Fallback to local store if no server anomalies
  const localAnomalies = currentPhoto?.file ? getAnomaliesByPhoto(currentPhoto.file.name) : [];
  const photoAnomalies = serverAnomalies.length > 0 ? serverAnomalies : localAnomalies;

  // Get the anomaly being edited (search in server anomalies first, then local)
  const editingAnomaly = useMemo(() => {
    if (!editingAnomalyId) return null;
    // Search in server anomalies
    const serverAnomaly = serverAnomalies.find((a: { id: string }) => a.id === editingAnomalyId);
    if (serverAnomaly) return serverAnomaly;
    // Fallback to local anomalies
    return anomalies.find(a => a.id === editingAnomalyId) || null;
  }, [editingAnomalyId, serverAnomalies, anomalies]);

  // Reset rectangle state when annotation mode changes
  useEffect(() => {
    if (annotationMode !== 'rectangle') {
      setRectStartPoint(null);
      setRectEndPoint(null);
    }
  }, [annotationMode]);

  // Convert anomalies to annotations format for display (with IDs for editing)
  const existingAnnotations = useMemo(() => {
    return photoAnomalies.map(a => {
      // Check if anomaly has geometry (server format) or annotations (local format)
      if (a.geometry) {
        // Server format
        const geom = a.geometry;
        if (geom.type === 'point') {
          return {
            id: a.id,
            type: 'Point' as const,
            points: [{ x: geom.coordinates[0], y: geom.coordinates[1] }],
          };
        } else if (geom.type === 'polygon' || geom.type === 'rectangle') {
          return {
            id: a.id,
            type: 'Polygon' as const,
            points: geom.coordinates.map((coord: number[]) => ({ x: coord[0], y: coord[1] })),
          };
        }
      }
      
      // Local format fallback
      if (a.annotations && a.annotations.length > 0) {
        return {
          id: a.id,
          ...a.annotations[0],
        };
      }
      
      // Fallback to pixelX/pixelY
      return {
        id: a.id,
        type: 'Point' as const,
        points: [{ x: a.pixelX || 0, y: a.pixelY || 0 }],
      };
    });
  }, [photoAnomalies]);

  // Track if image is ready for display
  const [isImageReady, setIsImageReady] = useState(false);

  // Reset view and image source when photo changes
  useEffect(() => {
    if (currentPhoto?.dataUrl) {
      setIsImageReady(false);
      setImageSrc(currentPhoto.dataUrl);
      setImageLoadError(false);
      
      // Preload image to get dimensions and wait for it to be ready
      const img = new Image();
      img.onload = () => {
        // Update currentPhoto with actual dimensions if not set
        if (currentPhoto && (!currentPhoto.width || !currentPhoto.height)) {
          currentPhoto.width = img.naturalWidth;
          currentPhoto.height = img.naturalHeight;
        }
        setIsImageReady(true);
        // Small delay to ensure DOM is updated
        requestAnimationFrame(() => {
          fitToView();
        });
      };
      img.onerror = () => {
        setIsImageReady(true);
        setImageLoadError(true);
      };
      img.src = currentPhoto.dataUrl;
    }
  }, [currentPhoto?.dataUrl]);

  // Handle image load error - try to reload from server
  const handleImageError = useCallback(async () => {
    if (isLoadingImage || imageLoadError) return;
    
    // Find the photo in store by filename
    const photo = photos.find(p => p.filename === currentPhoto?.file?.name);
    if (!photo) {
      setImageLoadError(true);
      return;
    }
    
    setIsLoadingImage(true);
    try {
      const photoUrl = await getPhotoUrl(photo.id, false);
      if (photoUrl) {
        setImageSrc(photoUrl);
        setImageLoadError(false);
      } else {
        setImageLoadError(true);
      }
    } catch (e) {
      console.warn('Failed to reload image from server:', e);
      setImageLoadError(true);
    } finally {
      setIsLoadingImage(false);
    }
  }, [isLoadingImage, imageLoadError, photos, currentPhoto, getPhotoUrl]);

  // Auto-select element when viewing a photo that's bound to an element
  useEffect(() => {
    if (!currentPhoto) return;
    
    // Find the photo in the store by filename
    const photo = photos.find(p => p.filename === currentPhoto.file.name);
    if (photo?.elementId && photo?.elementType) {
      // Only select if not already selected
      if (selectedElementId !== photo.elementId) {
        selectElement(photo.elementId, photo.elementType);
      }
    }
  }, [currentPhoto, photos, selectedElementId, selectElement]);

  // Scroll gallery to current photo
  useEffect(() => {
    if (currentPhotoIndex >= 0 && galleryRef.current) {
      const thumbnails = galleryRef.current.children;
      const currentThumbnail = thumbnails[currentPhotoIndex] as HTMLElement;
      if (currentThumbnail) {
        currentThumbnail.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest', 
          inline: 'center' 
        });
      }
    }
  }, [currentPhotoIndex]);

  const fitToView = useCallback(() => {
    if (!containerRef.current || !currentPhoto) return;
    
    const container = containerRef.current;
    const containerWidth = container.clientWidth;
    const containerHeight = container.clientHeight;
    
    const scaleX = containerWidth / currentPhoto.width;
    const scaleY = containerHeight / currentPhoto.height;
    const newScale = Math.min(scaleX, scaleY, 1) * 0.9;
    
    setScale(newScale);
    setPosition({
      x: (containerWidth - currentPhoto.width * newScale) / 2,
      y: (containerHeight - currentPhoto.height * newScale) / 2,
    });
  }, [currentPhoto]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.1, Math.min(5, scale * delta));
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (rect) {
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      
      const newX = mouseX - (mouseX - position.x) * (newScale / scale);
      const newY = mouseY - (mouseY - position.y) * (newScale / scale);
      
      setScale(newScale);
      setPosition({ x: newX, y: newY });
    }
  }, [scale, position]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button !== 0) return;
    
    // Always reset drag tracking on mouse down
    didDragRef.current = false;
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };
    
    // Don't start drag if in annotation mode
    if (annotationMode) return;
    
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
  }, [position, annotationMode]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    // Track coordinates for HUD display
    const rect = imageRef.current?.getBoundingClientRect();
    if (rect && currentPhoto) {
      const x = Math.round((e.clientX - rect.left) / scale);
      const y = Math.round((e.clientY - rect.top) / scale);
      
      // Only show coords if within image bounds
      if (x >= 0 && x <= currentPhoto.width && y >= 0 && y <= currentPhoto.height) {
        setMouseCoords({ x, y });
        
        // Update rectangle preview if in rectangle mode and start point is set
        if (annotationMode === 'rectangle' && rectStartPoint) {
          setRectEndPoint({ x, y });
        }
      } else {
        setMouseCoords(null);
      }
    }
    
    // Handle dragging
    if (!isDragging) return;
    
    // Check if mouse moved enough to count as drag (not just a click)
    const dx = e.clientX - dragStartPosRef.current.x;
    const dy = e.clientY - dragStartPosRef.current.y;
    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      didDragRef.current = true;
    }
    
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  }, [isDragging, dragStart, scale, currentPhoto, annotationMode, rectStartPoint]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
    setMouseCoords(null);
  }, []);

  const handleImageClick = useCallback((e: React.MouseEvent) => {
    // Skip if actual drag movement happened (not just a click)
    if (didDragRef.current) {
      return;
    }
    
    // Check if element is selected
    if (!selectedElementId || !selectedElementType) {
      // Try to auto-select element if current photo is bound to one
      if (currentPhotoData?.elementId && currentPhotoData?.elementType) {
        selectElement(currentPhotoData.elementId, currentPhotoData.elementType);
        // Retry the click after a short delay to allow state to update
        setTimeout(() => {
          handleImageClick(e);
        }, 100);
        return;
      }
      
      // If still no element selected, show user-friendly message
      toast.warning('Please select an element first', {
        description: 'Go to Map tab, click on a segment or support, then return to this photo',
      });
      return;
    }
    
    const rect = imageRef.current?.getBoundingClientRect();
    if (!rect || !currentPhoto) return;
    
    const clickX = e.clientX - rect.left;
    const clickY = e.clientY - rect.top;
    
    const pixelX = Math.round(clickX / scale);
    const pixelY = Math.round(clickY / scale);
    
    if (pixelX < 0 || pixelX > currentPhoto.width || pixelY < 0 || pixelY > currentPhoto.height) {
      return;
    }

    if (annotationMode === 'point') {
      // Point mode - add single point and open form
      openAnomalyForm(pixelX, pixelY);
      setAnnotationMode(null);
    } else if (annotationMode === 'rectangle') {
      // Rectangle mode - first click sets start, second click sets end
      if (!rectStartPoint) {
        setRectStartPoint({ x: pixelX, y: pixelY });
        setRectEndPoint(null);
      } else {
        // Second click - create rectangle and open form
        const points = [
          { x: rectStartPoint.x, y: rectStartPoint.y },
          { x: pixelX, y: rectStartPoint.y },
          { x: pixelX, y: pixelY },
          { x: rectStartPoint.x, y: pixelY },
        ];
        // Note: Don't call setAnnotationMode(null) here as it clears pendingAnnotationPoints!
        // openAnomalyFormWithAnnotation already sets the form state with points
        openAnomalyFormWithAnnotation(points, 'Rectangle');
        setRectStartPoint(null);
        setRectEndPoint(null);
      }
    } else if (annotationMode === 'polygon') {
      // Polygon mode - add point to pending polygon
      addAnnotationPoint({ x: pixelX, y: pixelY });
    }
    // No action in pan mode (annotationMode === null) - just drag/pan the image
  }, [selectedElementId, selectedElementType, scale, currentPhoto, annotationMode, openAnomalyForm, addAnnotationPoint, setAnnotationMode, currentPhotoData, selectElement, rectStartPoint, openAnomalyFormWithAnnotation]);

  const handleImageDoubleClick = useCallback((e: React.MouseEvent) => {
    if (annotationMode === 'polygon' && pendingAnnotationPoints.length >= 3) {
      e.preventDefault();
      e.stopPropagation();
      // Finish polygon and open form
      openAnomalyFormWithAnnotation(pendingAnnotationPoints, 'Polygon');
    }
  }, [annotationMode, pendingAnnotationPoints, openAnomalyFormWithAnnotation]);

  const handleFinishPolygon = useCallback(() => {
    if (pendingAnnotationPoints.length >= 3) {
      openAnomalyFormWithAnnotation(pendingAnnotationPoints, 'Polygon');
    }
  }, [pendingAnnotationPoints, openAnomalyFormWithAnnotation]);

  const handleAnomalySubmit = useCallback(async (data: AnomalyFormData) => {
    // Get fresh values from store to avoid stale closure
    const storeState = useUIStore.getState();
    const currentPendingAnnotationType = storeState.pendingAnnotationType;
    const currentPendingAnnotationPoints = storeState.pendingAnnotationPoints;
    const currentPendingAnomalyCoords = storeState.pendingAnomalyCoords;
    
    // Edit mode - update existing anomaly
    if (editingAnomalyId && editingAnomaly) {
      try {
        await anomaliesApi.update(editingAnomalyId, {
          code: data.issueCode,
          level: data.evaluationLevel,
          description: data.note,
        });
        closeAnomalyForm();
        toast.success('Anomaly updated successfully');
        // Reload anomalies to show the updated one
        await reloadAnomalies();
      } catch (error) {
        console.error('Failed to update anomaly:', error);
        toast.error('Failed to update anomaly', {
          description: 'Please try again',
        });
      }
      return;
    }

    // Create mode - add new anomaly
    if (!currentPhoto || !currentPhoto.id || !selectedElementId || !currentPendingAnomalyCoords) {
      console.error('Missing required data for anomaly creation', { 
        hasPhoto: !!currentPhoto, 
        photoId: currentPhoto?.id,
        elementId: selectedElementId,
        coords: currentPendingAnomalyCoords 
      });
      toast.error('Missing required data', {
        description: 'Please select an element and click on the photo',
      });
      return;
    }
    
    try {
      // Prepare geometry based on annotation type
      let geometry: { type: 'point' | 'rectangle' | 'polygon'; coordinates: number[] | number[][] };
      
      if (currentPendingAnnotationType === 'Rectangle' && currentPendingAnnotationPoints.length === 4) {
        // Rectangle - store as rectangle type with 4 corner points
        geometry = {
          type: 'rectangle' as const,
          coordinates: currentPendingAnnotationPoints.map(p => [p.x, p.y]),
        };
      } else if (currentPendingAnnotationType === 'Polygon' && currentPendingAnnotationPoints.length >= 3) {
        // Polygon - store as polygon type
        geometry = {
          type: 'polygon' as const,
          coordinates: currentPendingAnnotationPoints.map(p => [p.x, p.y]),
        };
      } else {
        // Point - single coordinate
        geometry = {
          type: 'point' as const,
          coordinates: [currentPendingAnomalyCoords.x, currentPendingAnomalyCoords.y],
        };
      }

      // Create anomaly via API
      await anomaliesApi.create({
        photoId: String(currentPhoto.id),
        elementId: selectedElementId ? String(selectedElementId) : undefined,
        code: data.issueCode,
        level: data.evaluationLevel,
        description: data.note || data.issueDescription,
        geometry,
      });

      closeAnomalyForm();
      toast.success('Anomaly created successfully');
      
      // Reload anomalies to show the new one
      await reloadAnomalies();
      
      // Trigger auto-advance in fast marking mode
      advanceToNextPhoto();
    } catch (error) {
      console.error('Failed to create anomaly:', error);
      toast.error('Failed to create anomaly', {
        description: 'Please try again',
      });
    }
  }, [editingAnomalyId, editingAnomaly, currentPhoto, selectedElementId, closeAnomalyForm, advanceToNextPhoto, reloadAnomalies]);

  // Handle anomaly deletion
  const handleAnomalyDelete = useCallback(async () => {
    if (!editingAnomalyId) return;
    
    try {
      await anomaliesApi.delete(editingAnomalyId);
      closeAnomalyForm();
      toast.success('Anomaly deleted');
      await reloadAnomalies();
    } catch (error) {
      console.error('Failed to delete anomaly:', error);
      toast.error('Failed to delete anomaly');
    }
  }, [editingAnomalyId, closeAnomalyForm, reloadAnomalies]);

  // Keyboard shortcuts
  usePhotoViewerShortcuts({
    onFitToView: fitToView,
    onZoomIn: zoomIn,
    onZoomOut: zoomOut,
    onResetView: resetView,
    onFinishPolygon: handleFinishPolygon,
    onPrevPhoto: goPrevPhoto,
    onNextPhoto: goNextPhoto,
    onShowOnMap: goToMapLocation,
  });

  if (!currentPhoto || !currentPhoto.file || !currentPhoto.dataUrl) {
    return (
      <div className="h-full flex items-center justify-center text-gray-400">
        <p>Select an element and add a photo</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-100 dark:bg-slate-950 transition-colors">
      {/* Aviation-Style Toolbar */}
      <div className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700/50 px-3 py-1.5 flex items-center gap-2">
        {/* Close Button */}
        <button
          onClick={() => setCurrentPhoto(null)}
          className="aviation-btn p-1.5 rounded"
          title="Close (Esc)"
        >
          <X className="w-4 h-4" />
        </button>
        
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700/50 mx-1" />
        
        {/* Zoom Controls */}
        <div className="flex items-center gap-1">
          <button onClick={zoomOut} className="aviation-btn p-1.5 rounded" title="Zoom Out (-)">
            <ZoomOut className="w-4 h-4" />
          </button>
          <div className="hud-panel px-2 py-1 rounded min-w-[52px] text-center">
            <span className="text-slate-700 dark:text-slate-300 font-mono text-xs">{Math.round(scale * 100)}%</span>
          </div>
          <button onClick={zoomIn} className="aviation-btn p-1.5 rounded" title="Zoom In (+)">
            <ZoomIn className="w-4 h-4" />
          </button>
          <button onClick={fitToView} className="aviation-btn p-1.5 rounded" title="Fit to View (Space)">
            <Maximize className="w-4 h-4" />
          </button>
          <button onClick={resetView} className="aviation-btn p-1.5 rounded" title="Reset View (R)">
            <RotateCcw className="w-4 h-4" />
          </button>
        </div>

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700/50 mx-1" />

        {/* Tools */}
        <div className="flex items-center gap-1">
          <span className="text-[9px] text-slate-400 dark:text-slate-500 uppercase tracking-wider font-mono mr-1">TOOLS</span>
          <button
            onClick={() => setAnnotationMode(null)}
            className={`aviation-btn p-1.5 rounded ${annotationMode === null ? 'active' : ''}`}
            title="Pan / Move (H)"
          >
            <Hand className="w-4 h-4" />
          </button>
          <button
            onClick={() => setAnnotationMode('point')}
            className={`aviation-btn p-1.5 rounded ${annotationMode === 'point' ? 'active' : ''}`}
            title="Point Anomaly (P)"
          >
            <Circle className="w-4 h-4" />
          </button>
          <button
            onClick={() => setAnnotationMode('rectangle')}
            className={`aviation-btn p-1.5 rounded ${annotationMode === 'rectangle' ? 'active' : ''}`}
            title="Rectangle Area (B)"
          >
            <Square className="w-4 h-4" />
          </button>
          <button
            onClick={() => setAnnotationMode('polygon')}
            className={`aviation-btn p-1.5 rounded ${annotationMode === 'polygon' ? 'active' : ''}`}
            title="Polygon Anomaly (G)"
          >
            <Pentagon className="w-4 h-4" />
          </button>
        </div>

        {/* Polygon Drawing Status */}
        {annotationMode === 'polygon' && pendingAnnotationPoints.length > 0 && (
          <div className="flex items-center gap-1 ml-1">
            <span className="tech-badge tech-badge-blue">
              {pendingAnnotationPoints.length} PTS
            </span>
            <button onClick={undoLastPoint} className="aviation-btn p-1 rounded" title="Undo (Backspace)">
              <Undo2 className="w-3.5 h-3.5" />
            </button>
            {pendingAnnotationPoints.length >= 3 && (
              <button
                onClick={handleFinishPolygon}
                className="p-1 bg-green-600/80 hover:bg-green-500 text-white rounded transition-colors"
                title="Finish (Enter)"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={clearPendingAnnotation}
              className="p-1 hover:bg-red-500/30 text-red-400 rounded transition-colors"
              title="Cancel (Escape)"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Rectangle Drawing Status */}
        {annotationMode === 'rectangle' && rectStartPoint && (
          <div className="flex items-center gap-1 ml-1">
            <span className="tech-badge tech-badge-blue">
              {rectEndPoint ? '2/2' : '1/2'} PTS
            </span>
            <button
              onClick={() => { setRectStartPoint(null); setRectEndPoint(null); }}
              className="p-1 hover:bg-red-500/30 text-red-400 rounded transition-colors"
              title="Cancel (Escape)"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700/50 mx-1" />
        
        {/* Element Status - clickable to go to map */}
        {selectedElementId && selectedElementType ? (
          <button
            onClick={goToMapLocation}
            className="tech-badge tech-badge-green flex items-center gap-1 cursor-pointer hover:bg-green-500/30 transition-colors"
            title="Go to element on map"
          >
            <Link2 className="w-3 h-3" />
            <span>{selectedElementType}</span>
            <Map className="w-3 h-3 ml-1 opacity-60" />
          </button>
        ) : (
          <div className="tech-badge tech-badge-amber flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            <span>NO ELEM</span>
          </div>
        )}

        {/* Spacer */}
        <div className="flex-1" />
      </div>

      {/* Image container with aviation grid background */}
      <div
        ref={containerRef}
        className={`flex-1 overflow-hidden aviation-grid-bg dark:aviation-grid-bg-dark relative ${
          annotationMode ? 'cursor-crosshair' : 'cursor-grab'
        }`}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
      >
        {/* Loading overlay */}
        {!isImageReady && !imageLoadError && (
          <div className="absolute inset-0 flex items-center justify-center bg-slate-200 dark:bg-slate-900 z-10">
            <div className="text-center">
              <div className="w-12 h-12 border-2 border-slate-300 dark:border-slate-600 border-t-cyan-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400 text-sm font-mono uppercase tracking-wider">Loading...</p>
            </div>
          </div>
        )}
        
        <div
          style={{
            transform: `translate(${position.x}px, ${position.y}px)`,
            transformOrigin: '0 0',
            opacity: isImageReady ? 1 : 0,
            transition: 'opacity 0.15s ease-in-out',
          }}
          className="relative"
        >
          {imageLoadError ? (
            <div 
              className="flex flex-col items-center justify-center bg-gray-800 text-gray-400"
              style={{ width: currentPhoto.width || 800, height: currentPhoto.height || 600 }}
            >
              <ImageOff className="w-16 h-16 mb-4" />
              <p>Failed to load image</p>
              <p className="text-sm mt-2">{currentPhoto.file.name}</p>
              <button 
                onClick={() => {
                  setImageLoadError(false);
                  setIsImageReady(false);
                  setImageSrc(currentPhoto.dataUrl);
                }}
                className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              <img
                ref={imageRef}
                src={imageSrc || currentPhoto.dataUrl}
                alt="Inspection photo"
                style={{
                  transform: `scale(${scale})`,
                  transformOrigin: '0 0',
                }}
                onClick={handleImageClick}
                onDoubleClick={handleImageDoubleClick}
                onError={handleImageError}
                draggable={false}
                className="max-w-none"
              />
              
              {/* Corner Markers */}
              <svg 
                className="absolute inset-0 pointer-events-none"
                style={{
                  width: currentPhoto.width * scale,
                  height: currentPhoto.height * scale,
                }}
              >
                {/* Top-Left Corner */}
                <g stroke="rgba(148, 163, 184, 0.6)" strokeWidth="2" fill="none">
                  <path d={`M 0 20 L 0 0 L 20 0`} />
                </g>
                {/* Top-Right Corner */}
                <g stroke="rgba(148, 163, 184, 0.6)" strokeWidth="2" fill="none">
                  <path d={`M ${currentPhoto.width * scale - 20} 0 L ${currentPhoto.width * scale} 0 L ${currentPhoto.width * scale} 20`} />
                </g>
                {/* Bottom-Left Corner */}
                <g stroke="rgba(148, 163, 184, 0.6)" strokeWidth="2" fill="none">
                  <path d={`M 0 ${currentPhoto.height * scale - 20} L 0 ${currentPhoto.height * scale} L 20 ${currentPhoto.height * scale}`} />
                </g>
                {/* Bottom-Right Corner */}
                <g stroke="rgba(148, 163, 184, 0.6)" strokeWidth="2" fill="none">
                  <path d={`M ${currentPhoto.width * scale - 20} ${currentPhoto.height * scale} L ${currentPhoto.width * scale} ${currentPhoto.height * scale} L ${currentPhoto.width * scale} ${currentPhoto.height * scale - 20}`} />
                </g>
              </svg>
            </>
          )}
          
          {/* Annotation layer */}
          <AnnotationLayer
            annotations={existingAnnotations}
            pendingPoints={pendingAnnotationPoints}
            scale={scale}
            annotationMode={annotationMode}
            rectStartPoint={rectStartPoint}
            rectEndPoint={rectEndPoint}
            onAnomalyClick={(anomalyId) => editAnomaly(anomalyId)}
          />
        </div>
        
        {/* HUD Overlays */}
        
        {/* Top-Left: Mouse Coordinates */}
        <div className="absolute top-3 left-3 pointer-events-none">
          <div className="hud-panel px-3 py-2 rounded">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">Cursor</div>
            {mouseCoords ? (
              <div className="font-mono text-sm">
                <span className="text-slate-400">X:</span>
                <span className="text-cyan-400 ml-1">{String(mouseCoords.x).padStart(4, '0')}</span>
                <span className="text-slate-600 mx-2">|</span>
                <span className="text-slate-400">Y:</span>
                <span className="text-cyan-400 ml-1">{String(mouseCoords.y).padStart(4, '0')}</span>
              </div>
            ) : (
              <div className="font-mono text-sm text-slate-600">----  |  ----</div>
            )}
          </div>
        </div>

        {/* Top-Right: Scale & Dimensions */}
        <div className="absolute top-3 right-3 pointer-events-none">
          <div className="hud-panel px-3 py-2 rounded text-right">
            <div className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">View</div>
            <div className="font-mono text-sm">
              <span className="text-slate-400">Scale:</span>
              <span className="text-emerald-400 ml-1">{Math.round(scale * 100)}%</span>
            </div>
            <div className="font-mono text-xs text-slate-500 mt-0.5">
              {currentPhoto.width} × {currentPhoto.height}
            </div>
          </div>
        </div>

        {/* Bottom-Left: File Info */}
        {currentPhotoData && (
          <div className="absolute bottom-3 left-3 pointer-events-none">
            <div className="hud-panel px-3 py-2 rounded max-w-xs">
              <div className="flex items-center gap-2 mb-1">
                <span className={`tech-badge ${
                  currentPhotoData.camera === 'FRONTE' ? 'tech-badge-blue' : 'tech-badge-amber'
                }`}>
                  {currentPhotoData.camera || 'CAM'}
                </span>
                {currentPhotoData.gps && (
                  <span className="tech-badge tech-badge-green">GPS</span>
                )}
              </div>
              <div className="font-mono text-xs text-slate-400 truncate" title={currentPhotoData.filename}>
                {currentPhotoData.filename}
              </div>
              {currentPhotoData.dateTaken && (
                <div className="font-mono text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(currentPhotoData.dateTaken).toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit', 
                    second: '2-digit',
                    hour12: false
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Bottom-Right: Anomalies Count */}
        {photoAnomalies.length > 0 && (
          <div className="absolute bottom-3 right-3 pointer-events-none">
            <div className="hud-panel px-3 py-2 rounded">
              <span className="tech-badge tech-badge-red flex items-center gap-1">
                <AlertTriangle className="w-3 h-3" />
                {photoAnomalies.length} ANOMAL{photoAnomalies.length === 1 ? 'Y' : 'IES'}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Aviation-Style Navigation Strip */}
      {photos.length > 0 && (
        <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700/50">
          {/* Film Strip Header */}
          <div className="flex items-center justify-between px-3 py-1.5 border-b border-slate-100 dark:border-slate-800/50">
            {/* Left: Navigation */}
            <div className="flex items-center gap-2">
              <button
                onClick={goPrevPhoto}
                disabled={!canGoPrev}
                className="aviation-btn px-2 py-1 rounded flex items-center gap-1"
                title="Previous (←)"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <div className="hud-panel px-3 py-1 rounded flex items-center gap-2">
                <span className="text-[9px] text-slate-400 dark:text-slate-500 uppercase tracking-wider">Frame</span>
                <span className="font-mono text-sm">
                  <span className="text-cyan-600 dark:text-cyan-400">{String(currentPhotoIndex + 1).padStart(3, '0')}</span>
                  <span className="text-slate-400 dark:text-slate-600 mx-1">/</span>
                  <span className="text-slate-500 dark:text-slate-400">{String(photos.length).padStart(3, '0')}</span>
                </span>
              </div>
              
              <button
                onClick={goNextPhoto}
                disabled={!canGoNext}
                className="aviation-btn px-2 py-1 rounded flex items-center gap-1"
                title="Next (→)"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            {/* Center: Keyboard hints */}
            <div className="hidden lg:flex items-center gap-3 text-[9px] text-slate-400 dark:text-slate-500 font-mono uppercase tracking-wider">
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-slate-500 dark:text-slate-400">←</kbd>
                <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-slate-500 dark:text-slate-400">→</kbd>
                <span className="ml-1">Nav</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-slate-500 dark:text-slate-400">M</kbd>
                <span className="ml-1">Map</span>
              </div>
              <div className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded text-slate-500 dark:text-slate-400">P</kbd>
                <span className="ml-1">Point</span>
              </div>
            </div>

            {/* Right: Review and Map buttons */}
            <div className="flex items-center gap-2">
              {/* Review button */}
              {currentPhoto?.id && (
                isReviewed ? (
                  <div className="flex items-center gap-1.5 px-2.5 py-1 bg-green-600/20 text-green-400 border border-green-500/40 rounded text-xs font-mono uppercase tracking-wide">
                    <CheckCircle2 className="w-3.5 h-3.5 fill-green-400/30" />
                    <span className="hidden sm:inline">Reviewed</span>
                  </div>
                ) : (
                  <button
                    onClick={handleToggleReviewed}
                    disabled={isMarkingReviewed}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-700/50 hover:bg-slate-600/50 text-slate-300 border border-slate-600/50 rounded text-xs font-mono uppercase tracking-wide transition-colors disabled:opacity-50"
                    title="Mark as reviewed (R)"
                  >
                    {isMarkingReviewed ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <CheckCircle2 className="w-3.5 h-3.5" />
                    )}
                    <span className="hidden sm:inline">Review</span>
                  </button>
                )
              )}

              {/* Map button */}
              {currentPhotoData && (currentPhotoData.gps || currentPhotoData.elementId) && (
                <button
                  onClick={goToMapLocation}
                  className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-600/20 hover:bg-emerald-500/30 text-emerald-400 border border-emerald-500/40 rounded text-xs font-mono uppercase tracking-wide transition-colors"
                  title="Show on Map (M)"
                >
                  <Navigation className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Locate</span>
                </button>
              )}
            </div>
          </div>

          {/* Film Strip Thumbnails */}
          <div className="px-2 py-2 h-[76px] bg-slate-50 dark:bg-slate-950/50">
            <div 
              ref={galleryRef}
              className="flex gap-2 overflow-x-auto h-full items-center px-1"
              style={{ scrollbarWidth: 'thin', scrollbarColor: '#94a3b8 #f1f5f9' }}
            >
              {photos.map((photo, index) => {
                const isActive = index === currentPhotoIndex;
                const hasAnomalies = serverAnomalies.some(a => a.photoId === photo.id) || 
                  localAnomalies.some(a => a.photoFileName === photo.filename);
                
                return (
                  <div
                    key={photo.id}
                    className={`
                      relative flex-shrink-0 w-16 h-12 rounded overflow-hidden 
                      cursor-pointer group transition-all duration-150
                      border-2
                      ${isActive 
                        ? 'border-cyan-500 shadow-lg shadow-cyan-500/20' 
                        : 'border-slate-200 dark:border-slate-700/50 opacity-60 hover:opacity-100 hover:border-slate-300 dark:hover:border-slate-600'
                      }
                    `}
                    title={`Frame ${String(index + 1).padStart(3, '0')}${hasAnomalies ? ' — ANOMALY' : ''}`}
                    onClick={() => goToPhoto(index)}
                  >
                    <PhotoThumbnail
                      photoId={photo.id}
                      src={photo.thumbnailUrl}
                      alt={photo.filename}
                      className="w-full h-full object-cover"
                    />
                    
                    {/* Frame number */}
                    <div 
                      className={`absolute bottom-0 left-0 right-0 px-1 py-0.5 text-center frame-number
                        ${isActive 
                          ? 'bg-cyan-500/90 text-white' 
                          : 'bg-white/80 dark:bg-slate-900/80 text-slate-500 dark:text-slate-400 group-hover:bg-slate-100 dark:group-hover:bg-slate-800'
                        }`}
                    >
                      {String(index + 1).padStart(3, '0')}
                    </div>
                    
                    {/* Anomaly indicator */}
                    {hasAnomalies && (
                      <div className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-red-500 rounded-sm border border-red-400 animate-pulse" />
                    )}
                    
                    {/* Active frame indicator */}
                    {isActive && (
                      <div className="absolute top-0 left-0 right-0 h-0.5 bg-cyan-400" />
                    )}
                    
                    {/* GPS indicator */}
                    {photo.gps && (
                      <div className={`absolute top-0.5 left-0.5 ${isActive ? 'opacity-80' : 'opacity-60'}`}>
                        <MapPin className="w-2.5 h-2.5 text-emerald-400 drop-shadow" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Anomaly form modal */}
      {isAnomalyFormOpen && (selectedElementType || editingAnomaly) && (
        <AnomalyForm
          elementType={editingAnomaly?.elementType || selectedElementType!}
          onSubmit={handleAnomalySubmit}
          onClose={closeAnomalyForm}
          onDelete={editingAnomaly ? handleAnomalyDelete : undefined}
          editingAnomaly={editingAnomaly}
        />
      )}
    </div>
  );
}
